import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useLoads, useRevenueCategories } from '../hooks/useApi'
import { formatCurrency, formatDate, getCurrentDateString } from '../lib/utils'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { toast } from 'sonner'
import { Plus, Truck, Edit2, Trash2, Loader2, MapPin, ChevronRight, TrendingUp, Camera, Upload, Mail, PenLine, X, DollarSign } from 'lucide-react'
import { Switch } from '../components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

// Default empty line item
const createEmptyLineItem = () => ({
  category: 'linehaul',
  amount: '',
  is_reimbursement: false
})

// Helper functions for date filtering
const getStartOfWeek = () => {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(now.setDate(diff))
  return monday.toISOString().split('T')[0]
}

const getStartOfMonth = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
}

const getStartOfQuarter = () => {
  const now = new Date()
  const quarter = Math.floor(now.getMonth() / 3)
  return new Date(now.getFullYear(), quarter * 3, 1).toISOString().split('T')[0]
}

const getStartOfYear = () => {
  const now = new Date()
  return new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]
}

export default function Loads() {
  const [loads, setLoads] = useState([])
  const [editingLoad, setEditingLoad] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [timePeriod, setTimePeriod] = useState('month')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [showCustomDates, setShowCustomDates] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const { fetchLoads, createLoad, updateLoad, deleteLoad, loading } = useLoads()
  const { fetchRevenueCategories } = useRevenueCategories()
  const [revenueCategories, setRevenueCategories] = useState([])

  const [form, setForm] = useState({
    pickup_date: getCurrentDateString(),
    delivery_date: getCurrentDateString(),
    pickup_location: '',
    delivery_location: '',
    miles: '',
    broker: '',
    load_number: '',
    notes: '',
    line_items: [createEmptyLineItem()]  // Start with one line item
  })
  
  // Calculate running total from line items
  const totalRevenue = form.line_items.reduce((sum, item) => {
    const amount = parseFloat(item.amount) || 0
    return sum + amount
  }, 0)

  useEffect(() => {
    loadData()
    // Open dialog if coming from quick action
    if (searchParams.get('new') === 'true') {
      setDialogOpen(true)
      navigate('/loads', { replace: true })
    }
  }, [])

  const loadData = async () => {
    try {
      const [data, revCats] = await Promise.all([
        fetchLoads(),
        fetchRevenueCategories()
      ])
      setLoads(data)
      setRevenueCategories(revCats)
    } catch (err) {
      toast.error('Failed to load data')
    }
  }

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.pickup_location || !form.delivery_location) {
      toast.error('Please fill in pickup and delivery locations')
      return
    }
    
    // Validate line items - at least one with amount
    const validLineItems = form.line_items.filter(item => item.amount && parseFloat(item.amount) > 0)
    if (validLineItems.length === 0) {
      toast.error('Please add at least one line item with an amount')
      return
    }

    try {
      const loadData = {
        pickup_date: form.pickup_date,
        delivery_date: form.delivery_date,
        pickup_location: form.pickup_location,
        delivery_location: form.delivery_location,
        miles: form.miles ? parseFloat(form.miles) : null,
        broker: form.broker,
        load_number: form.load_number,
        notes: form.notes,
        line_items: validLineItems.map(item => ({
          category: item.category,
          amount: parseFloat(item.amount),
          is_reimbursement: item.is_reimbursement || false
        }))
      }

      if (editingLoad) {
        await updateLoad(editingLoad.id, loadData)
        toast.success('Load updated')
      } else {
        await createLoad(loadData)
        toast.success('Load added')
      }
      
      setDialogOpen(false)
      resetForm()
      loadData()
    } catch (err) {
      toast.error('Failed to save load')
    }
  }

  const handleEdit = (load) => {
    setEditingLoad(load)
    
    // Convert old format (single rate) to new format (line_items) if needed
    let lineItems = load.line_items
    if (!lineItems || lineItems.length === 0) {
      // Legacy load with just rate field
      lineItems = [{
        category: load.revenue_category || 'linehaul',
        amount: load.rate?.toString() || '',
        is_reimbursement: load.is_reimbursement || false
      }]
    } else {
      // Convert amounts to strings for form
      lineItems = lineItems.map(item => ({
        ...item,
        amount: item.amount?.toString() || ''
      }))
    }
    
    setForm({
      pickup_date: load.pickup_date,
      delivery_date: load.delivery_date,
      pickup_location: load.pickup_location,
      delivery_location: load.delivery_location,
      miles: load.miles?.toString() || '',
      broker: load.broker || '',
      load_number: load.load_number || '',
      notes: load.notes || '',
      line_items: lineItems
    })
    setDialogOpen(true)
  }

  const handleDelete = async () => {
    try {
      await deleteLoad(deleteId)
      toast.success('Load deleted')
      setDeleteId(null)
      loadData()
    } catch (err) {
      toast.error('Failed to delete')
    }
  }

  const resetForm = () => {
    setEditingLoad(null)
    setForm({
      pickup_date: getCurrentDateString(),
      delivery_date: getCurrentDateString(),
      pickup_location: '',
      delivery_location: '',
      miles: '',
      broker: '',
      load_number: '',
      notes: '',
      line_items: [createEmptyLineItem()]
    })
  }
  
  // Line item management
  const addLineItem = () => {
    setForm(f => ({
      ...f,
      line_items: [...f.line_items, createEmptyLineItem()]
    }))
  }
  
  const removeLineItem = (index) => {
    if (form.line_items.length > 1) {
      setForm(f => ({
        ...f,
        line_items: f.line_items.filter((_, i) => i !== index)
      }))
    }
  }
  
  const updateLineItem = (index, field, value) => {
    setForm(f => ({
      ...f,
      line_items: f.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }))
  }

  // Handle time period selection
  const handleTimePeriodChange = (period) => {
    setTimePeriod(period)
    setShowCustomDates(period === 'custom')
  }

  // Get date range based on time period
  const getDateRange = () => {
    if (timePeriod === 'custom' && customStartDate && customEndDate) {
      return { start: customStartDate, end: customEndDate }
    }
    
    const today = getCurrentDateString()
    let start, end
    
    switch (timePeriod) {
      case 'week':
        start = getStartOfWeek()
        end = today
        break
      case 'month':
        start = getStartOfMonth()
        end = today
        break
      case 'quarter':
        start = getStartOfQuarter()
        end = today
        break
      case 'ytd':
        start = getStartOfYear()
        end = today
        break
      case 'all':
        start = '2000-01-01'
        end = '2099-12-31'
        break
      default:
        start = getStartOfMonth()
        end = today
    }
    
    return { start, end }
  }

  // Filter loads by date range
  const { start: startDate, end: endDate } = getDateRange()
  const filteredLoads = loads.filter(l => l.pickup_date >= startDate && l.pickup_date <= endDate)

  // Calculate stats
  const listTotalRevenue = filteredLoads.reduce((sum, l) => sum + (l.rate || 0), 0)
  const totalMiles = filteredLoads.reduce((sum, l) => sum + (l.miles || 0), 0)
  const avgRatePerMile = totalMiles > 0 ? listTotalRevenue / totalMiles : 0

  // Group by broker
  const brokerTotals = filteredLoads.reduce((acc, l) => {
    const broker = l.broker || 'Direct'
    acc[broker] = (acc[broker] || 0) + (l.rate || 0)
    return acc
  }, {})
  const sortedBrokers = Object.entries(brokerTotals).sort((a, b) => b[1] - a[1])

  const periodLabels = {
    week: 'This Week',
    month: 'This Month',
    quarter: 'This Quarter',
    ytd: 'Year to Date',
    all: 'All Time',
    custom: customStartDate && customEndDate ? `${customStartDate} to ${customEndDate}` : 'Custom Range'
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="loads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight font-['Barlow_Condensed']">
            LOADS / REVENUE
          </h1>
          <p className="text-muted-foreground">
            Track and manage your loads
          </p>
        </div>
        
        {/* Add Menu Dialog */}
        <Dialog open={addMenuOpen} onOpenChange={setAddMenuOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-load-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Load / Revenue</DialogTitle>
              <DialogDescription>
                Choose how you want to add revenue
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 py-4">
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  navigate('/scanner?type=load')
                }}
                data-testid="load-scan-option"
              >
                <Camera className="w-5 h-5 text-blue-500" />
                <div className="text-left">
                  <p className="font-medium">Scan Document</p>
                  <p className="text-xs text-muted-foreground">Use camera to scan rate con</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  navigate('/scanner?type=load&upload=true')
                }}
                data-testid="load-upload-option"
              >
                <Upload className="w-5 h-5 text-blue-500" />
                <div className="text-left">
                  <p className="font-medium">Upload Image</p>
                  <p className="text-xs text-muted-foreground">Upload from device</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  navigate('/scanner?type=load&email=true')
                }}
                data-testid="load-email-option"
              >
                <Mail className="w-5 h-5 text-blue-500" />
                <div className="text-left">
                  <p className="font-medium">From Email</p>
                  <p className="text-xs text-muted-foreground">Paste rate con email</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="h-14 justify-start gap-3"
                onClick={() => {
                  setAddMenuOpen(false)
                  setDialogOpen(true)
                }}
                data-testid="load-manual-option"
              >
                <PenLine className="w-5 h-5 text-blue-500" />
                <div className="text-left">
                  <p className="font-medium">Manual Entry</p>
                  <p className="text-xs text-muted-foreground">Enter details manually</p>
                </div>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Manual Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 sm:p-6">
          <div className="p-6 pb-0 sm:p-0">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-xl">
                {editingLoad ? 'EDIT LOAD' : 'ADD NEW LOAD'}
              </DialogTitle>
            </DialogHeader>
          </div>
          
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 sm:px-0">
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Date *</Label>
                  <Input
                    type="date"
                    value={form.pickup_date}
                    onChange={(e) => setForm(f => ({ ...f, pickup_date: e.target.value }))}
                    className="h-12"
                    data-testid="load-form-pickup-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Delivery Date *</Label>
                  <Input
                    type="date"
                    value={form.delivery_date}
                    onChange={(e) => setForm(f => ({ ...f, delivery_date: e.target.value }))}
                    className="h-12"
                    data-testid="load-form-delivery-date"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Pickup Location *</Label>
                <Input
                  placeholder="e.g., Dallas, TX"
                  value={form.pickup_location}
                  onChange={(e) => setForm(f => ({ ...f, pickup_location: e.target.value }))}
                  className="h-12"
                  data-testid="load-form-pickup-location"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Delivery Location *</Label>
                <Input
                  placeholder="e.g., Chicago, IL"
                  value={form.delivery_location}
                  onChange={(e) => setForm(f => ({ ...f, delivery_location: e.target.value }))}
                  className="h-12"
                  data-testid="load-form-delivery-location"
                />
              </div>
              
              {/* Miles */}
              <div className="space-y-2">
                <Label>Miles</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={form.miles}
                  onChange={(e) => setForm(f => ({ ...f, miles: e.target.value }))}
                  className="h-12"
                  data-testid="load-form-miles"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Broker</Label>
                  <Input
                    placeholder="Broker name"
                    value={form.broker}
                    onChange={(e) => setForm(f => ({ ...f, broker: e.target.value }))}
                    className="h-12"
                    data-testid="load-form-broker"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Load #</Label>
                  <Input
                    placeholder="Reference #"
                    value={form.load_number}
                    onChange={(e) => setForm(f => ({ ...f, load_number: e.target.value }))}
                    className="h-12"
                    data-testid="load-form-load-number"
                  />
                </div>
              </div>
              
              {/* Revenue Line Items Section */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    Revenue Line Items
                  </Label>
                  <span className="text-sm text-muted-foreground">
                    {form.line_items.length} item{form.line_items.length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Line Items List */}
                <div className="space-y-3">
                  {form.line_items.map((item, index) => (
                    <div key={index} className="p-3 bg-secondary/30 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                          Line {index + 1}
                        </span>
                        {form.line_items.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`remove-line-item-${index}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                      
                      {/* Category Dropdown */}
                      <Select
                        value={item.category}
                        onValueChange={(value) => updateLineItem(index, 'category', value)}
                      >
                        <SelectTrigger className="h-11" data-testid={`line-item-category-${index}`}>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {revenueCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Amount Input */}
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={item.amount}
                          onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                          className="h-11 text-lg font-semibold flex-1"
                          data-testid={`line-item-amount-${index}`}
                        />
                      </div>
                      
                      {/* Reimbursement Toggle */}
                      <div className="flex items-center justify-between py-2 px-3 bg-background/50 rounded-md">
                        <span className="text-sm">Reimbursement / Non-income</span>
                        <Switch
                          checked={item.is_reimbursement}
                          onCheckedChange={(checked) => updateLineItem(index, 'is_reimbursement', checked)}
                          data-testid={`line-item-reimbursement-${index}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Add Line Item Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addLineItem}
                  className="w-full h-12 border-dashed border-2"
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Another Line Item
                </Button>
                
                {/* Running Total */}
                <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <span className="font-semibold text-green-700 dark:text-green-400">Total Revenue</span>
                  <span className="text-xl font-bold text-green-700 dark:text-green-400" data-testid="total-revenue">
                    {formatCurrency(totalRevenue)}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  placeholder="Any additional notes..."
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="h-12"
                  data-testid="load-form-notes"
                />
              </div>
            </div>
          </div>
          
          {/* Save Button - Sticky footer */}
          <div className="flex-shrink-0 p-4 sm:p-0 sm:pt-4 bg-background border-t sm:border-t-0 border-border">
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full h-14 sm:h-12 rounded-full font-bold text-lg sm:text-base bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="save-load-form-btn"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
              {editingLoad ? 'Update Load' : 'Save Load'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Time Period Selector */}
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {['week', 'month', 'quarter'].map((period) => (
            <Button
              key={period}
              variant={timePeriod === period ? 'default' : 'outline'}
              className="h-10"
              onClick={() => handleTimePeriodChange(period)}
              data-testid={`period-${period}`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant={timePeriod === 'ytd' ? 'default' : 'outline'}
            className="h-10"
            onClick={() => handleTimePeriodChange('ytd')}
            data-testid="period-ytd"
          >
            YTD
          </Button>
          <Button
            variant={timePeriod === 'all' ? 'default' : 'outline'}
            className="h-10"
            onClick={() => handleTimePeriodChange('all')}
            data-testid="period-all"
          >
            All Time
          </Button>
          <Button
            variant={timePeriod === 'custom' ? 'default' : 'outline'}
            className="h-10"
            onClick={() => handleTimePeriodChange('custom')}
            data-testid="period-custom"
          >
            Custom
          </Button>
        </div>
        
        {/* Custom Date Range Picker */}
        {showCustomDates && (
          <div className="grid grid-cols-2 gap-3 p-3 bg-secondary/50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="h-10"
                data-testid="custom-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="h-10"
                data-testid="custom-end-date"
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary Card */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{periodLabels[timePeriod]}</p>
              <p className="text-3xl font-bold text-blue-500 kpi-value">
                {formatCurrency(listTotalRevenue, 0)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {filteredLoads.length} load{filteredLoads.length !== 1 ? 's' : ''}
                {totalMiles > 0 && ` • ${totalMiles.toLocaleString()} miles`}
              </p>
            </div>
            <TrendingUp className="w-12 h-12 text-blue-500/50" />
          </div>
        </CardContent>
      </Card>

      {/* Stats Row */}
      {totalMiles > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-secondary/50">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground uppercase">Avg $/Mile</p>
              <p className="text-xl font-bold text-blue-500">${avgRatePerMile.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className="bg-secondary/50">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground uppercase">Total Miles</p>
              <p className="text-xl font-bold">{totalMiles.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Broker Breakdown */}
      {sortedBrokers.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 font-['Barlow_Condensed'] uppercase tracking-wide text-sm">
              By Broker
            </h3>
            <div className="space-y-2">
              {sortedBrokers.map(([broker, total]) => (
                <div key={broker} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                  <span className="text-sm">{broker}</span>
                  <span className="font-semibold text-blue-500">{formatCurrency(total)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loads List */}
      <div className="space-y-3">
        <h3 className="font-semibold font-['Barlow_Condensed'] uppercase tracking-wide text-sm text-muted-foreground">
          Recent Loads
        </h3>
        {filteredLoads.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Truck className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No loads for {periodLabels[timePeriod].toLowerCase()}</p>
              <p className="text-sm text-muted-foreground mb-4">
                Add loads from the Dashboard
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredLoads.map((load) => (
            <Card key={load.id} className="card-interactive" data-testid={`load-item-${load.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="category-icon bg-blue-500/10">
                        <Truck className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg text-blue-500">
                          +{formatCurrency(load.rate)}
                        </p>
                        {load.miles && (
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(load.rate / load.miles)}/mi
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center text-sm mb-1">
                      <MapPin className="w-3 h-3 mr-1 text-muted-foreground" />
                      <span className="truncate">{load.pickup_location}</span>
                      <ChevronRight className="w-3 h-3 mx-1 text-muted-foreground" />
                      <span className="truncate">{load.delivery_location}</span>
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      {formatDate(load.pickup_date)}
                      {load.broker && ` • ${load.broker}`}
                      {load.load_number && ` • #${load.load_number}`}
                    </p>
                  </div>
                  
                  <div className="flex gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(load)}
                      data-testid={`edit-load-${load.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(load.id)}
                      data-testid={`delete-load-${load.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Load?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this load record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-load">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-load">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
